import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { store } from './store/store';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/layouts/AdminLayout';
import DoctorLayout from './components/layouts/DoctorLayout';

// Admin Pages
import AdminLogin from './pages/admin/auth/Login';
import AdminDashboard from './pages/admin/dashboard/Dashboard';
import AdminTradeDashboard from './pages/admin/dashboard/TradeDashboard';
import AdminUsers from './pages/admin/users/Users';
import AdminUserForm from './pages/admin/users/UserForm';
import AdminUserView from './pages/admin/users/UserView';
import AdminDoctors from './pages/admin/doctors/Doctors';
import AdminDoctorForm from './pages/admin/doctors/DoctorForm';
import AdminItems from './pages/admin/items/Items';
import AdminItemForm from './pages/admin/items/ItemForm';
import AdminArticles from './pages/admin/articles/Articles';
import AdminArticleForm from './pages/admin/articles/ArticleForm';
import AdminArticleView from './pages/admin/articles/ArticleView';
import AdminArticleTags from './pages/admin/articles/ArticleTags';
import AdminExhibitions from './pages/admin/exhibitions/Exhibitions';
import AdminExhibitionForm from './pages/admin/exhibitions/ExhibitionForm';
import AdminExhibitionView from './pages/admin/exhibitions/ExhibitionView';
import AdminItemView from './pages/admin/items/ItemView';
import AdminActivities from './pages/admin/activities/Activities';
import AdminActivityForm from './pages/admin/activities/ActivityForm';
import AdminPages from './pages/admin/pages/Pages';
import AdminPageForm from './pages/admin/pages/PageForm';
import AdminPageView from './pages/admin/pages/PageView';
import AdminPackages from './pages/admin/packages/Packages';
import AdminPackageForm from './pages/admin/packages/PackageForm';
import AdminPackageView from './pages/admin/packages/PackageView';
import AdminSubscriptionHistory from './pages/admin/subscriptions/SubscriptionHistory';
import AdminStripeLogs from './pages/admin/subscriptions/StripeLogs';
import AdminCategories from './pages/admin/categories/Categories';
import AdminCategoryProperties from './pages/admin/categories/CategoryProperties';
import AdminCategorySubCategories from './pages/admin/categories/CategorySubCategories';
import AdminCategoryForm from './pages/admin/categories/CategoryForm';
import AdminCategoryView from './pages/admin/categories/CategoryView';
import AdminTopics from './pages/admin/topics/Topics';
import AdminTopicForm from './pages/admin/topics/TopicForm';
import AdminTopicView from './pages/admin/topics/TopicView';
import AdminCountries from './pages/admin/countries/Countries';
import AdminCountryForm from './pages/admin/countries/CountryForm';
import AdminCities from './pages/admin/cities/Cities';
import AdminCityForm from './pages/admin/cities/CityForm';
import AdminBanners from './pages/admin/banners/Banners';
import AdminBannerForm from './pages/admin/banners/BannerForm';
import AdminAdCategories from './pages/admin/ad-categories/AdCategories';
import AdminAdCategoryForm from './pages/admin/ad-categories/AdCategoryForm';
import AdminAdCategoryCustomFields from './pages/admin/ad-categories/AdCategoryCustomFields';
import AdminAdCategoryCustomFilters from './pages/admin/ad-categories/AdCategoryCustomFilters';
import AdminAdTypes from './pages/admin/ad-types/AdTypes';
import AdminAdTypeForm from './pages/admin/ad-types/AdTypeForm';
import AdminInterests from './pages/admin/interests/Interests';
import AdminInterestForm from './pages/admin/interests/InterestForm';
import AdminStrategicAdTypes from './pages/admin/ad-types/StrategicAdTypes';
import AdminStrategicAdTypeForm from './pages/admin/ad-types/StrategicAdTypeForm';
import AdminAdPackages from './pages/admin/ad-packages/AdPackages';
import AdminAdPackageForm from './pages/admin/ad-packages/AdPackageForm';
import AdminAds from './pages/admin/ads/Ads';
import AdminAdDetails from './pages/admin/ads/AdDetails';
import AdminSystemUsers from './pages/admin/system-users/SystemUsers';
import AdminSystemUserForm from './pages/admin/system-users/SystemUserForm';
import AdminRoles from './pages/admin/roles/Roles';
import AdminRoleForm from './pages/admin/roles/RoleForm';
import AdminRolePermissions from './pages/admin/roles/RolePermissions';
import AdminPermissions from './pages/admin/roles/Permissions';
import AdminAccount from './pages/admin/settings/Account';
import AdminSettings from './pages/admin/settings/Settings';
import AdminContacts from './pages/admin/contacts/Contacts';
import AdminVoucherBatches from './pages/admin/vouchers/VoucherBatches';
import AdminVoucherBatchForm from './pages/admin/vouchers/VoucherBatchForm';
import AdminBatchVouchers from './pages/admin/vouchers/BatchVouchers';
import AdminAgriculturalInfluencers from './pages/admin/influencers/AgriculturalInfluencers';
import AdminAgriculturalInfluencerForm from './pages/admin/influencers/AgriculturalInfluencerForm';
import AdminReviews from './pages/admin/reviews/Reviews';
import AdminReviewDetails from './pages/admin/reviews/ReviewDetails';
import AdminNotifications from './pages/admin/notifications/Notifications';
import AdminNotificationForm from './pages/admin/notifications/NotificationForm';
import AdminPromoCodes from './pages/admin/promo-codes/PromoCodes';
import AdminPromoCodeForm from './pages/admin/promo-codes/PromoCodeForm';
import AdminPromoCodeDetails from './pages/admin/promo-codes/PromoCodeDetails';
import AdminFinance from './pages/admin/finance/Finance';
import AdminActivityLogs from './pages/admin/activity-logs/ActivityLogs';

// Doctor Pages
import DoctorLogin from './pages/doctor/DoctorLogin';
import DoctorDashboard from './pages/doctor/DoctorDashboard';
import DoctorTickets from './pages/doctor/DoctorTickets';
import DoctorNewTickets from './pages/doctor/DoctorNewTickets';
import DoctorTicketChat from './pages/doctor/DoctorTicketChat';
import DoctorAccount from './pages/doctor/DoctorAccount';

// Mobile Webview Pages (no header/footer, for iframe/webview)
import MobileReportProblem from './pages/mobile/ReportProblem';
import MobileViolations from './pages/mobile/Violations';
import MobileReports from './pages/mobile/Reports';
import MobileReviews from './pages/mobile/Reviews';

// Landing Pages
import LandingLayout from './components/layouts/LandingLayout';
import Home from './pages/landing/Home';
import PrivacyPolicy from './pages/landing/PrivacyPolicy';
import TermsConditions from './pages/landing/TermsConditions';
import Contact from './pages/landing/Contact';
import About from './pages/landing/About';
import FAQs from './pages/landing/FAQs';
import HowWorks from './pages/landing/HowWorks';
import BecomeVendor from './pages/landing/BecomeVendor';
import Careers from './pages/landing/Careers';
import ReturnRefund from './pages/landing/ReturnRefund';
import Subscription from './pages/landing/Subscription';
import SubscriptionPayment from './pages/landing/SubscriptionPayment';
import MySubscription from './pages/landing/MySubscription';

function App() {
  const pathname = process.env.REACT_APP_SUB_FOLDER || '';
  return (

    <Provider store={store}>
      <Router basename={pathname}>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        <Routes>
          {/* Mobile Webview Routes — no layout wrapper */}
          <Route path="/mobile/report-problem" element={<MobileReportProblem />} />
          <Route path="/mobile/violations" element={<MobileViolations />} />
          <Route path="/mobile/reports" element={<MobileReports />} />
          <Route path="/mobile/reviews" element={<MobileReviews />} />

          {/* Landing Routes */}
          <Route path="/" element={<LandingLayout />}>
            <Route index element={<Home />} />
            <Route path="home" element={<Home />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-conditions" element={<TermsConditions />} />
            <Route path="contact" element={<Contact />} />
            <Route path="about" element={<About />} />
            <Route path="faqs" element={<FAQs />} />
            <Route path="how-works" element={<HowWorks />} />
            <Route path="become-vendor" element={<BecomeVendor />} />
            <Route path="careers" element={<Careers />} />
            <Route path="return-refund" element={<ReturnRefund />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="subscription/cards" element={<SubscriptionPayment />} />
            <Route path="my-subscription" element={<MySubscription />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedUserTypes={['admin']}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="" element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="trade-dashboard" element={<AdminTradeDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/add" element={<AdminUserForm />} />
            <Route path="users/:id" element={<AdminUserView />} />
            <Route path="users/:id/edit" element={<AdminUserForm />} />
            <Route path="doctors" element={<AdminDoctors />} />
            <Route path="doctors/add" element={<AdminDoctorForm />} />
            <Route path="doctors/:id/edit" element={<AdminDoctorForm />} />
            <Route path="items" element={<AdminItems />} />
            <Route path="items/add" element={<AdminItemForm />} />
            <Route path="items/:id" element={<AdminItemView />} />
            <Route path="items/:id/edit" element={<AdminItemForm />} />
            <Route path="articles" element={<AdminArticles />} />
            <Route path="articles/add" element={<AdminArticleForm />} />
            <Route path="articles/:id" element={<AdminArticleView />} />
            <Route path="articles/:id/edit" element={<AdminArticleForm />} />
            <Route path="article-tags" element={<AdminArticleTags />} />
            <Route path="exhibitions" element={<AdminExhibitions />} />
            <Route path="exhibitions/add" element={<AdminExhibitionForm />} />
            <Route path="exhibitions/:id" element={<AdminExhibitionView />} />
            <Route path="exhibitions/:id/edit" element={<AdminExhibitionForm />} />
            <Route path="activities" element={<AdminActivities />} />
            <Route path="activities/add" element={<AdminActivityForm />} />
            <Route path="activities/:id/edit" element={<AdminActivityForm />} />
            <Route path="pages" element={<AdminPages />} />
            <Route path="pages/add" element={<AdminPageForm />} />
            <Route path="pages/:id" element={<AdminPageView />} />
            <Route path="pages/:id/edit" element={<AdminPageForm />} />
            <Route path="packages" element={<AdminPackages />} />
            <Route path="packages/add" element={<AdminPackageForm />} />
            <Route path="packages/:id" element={<AdminPackageView />} />
            <Route path="packages/:id/edit" element={<AdminPackageForm />} />
            <Route path="subscription-history" element={<AdminSubscriptionHistory />} />
            <Route path="stripe-logs" element={<AdminStripeLogs />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="categories/add" element={<AdminCategoryForm />} />
            <Route path="categories/:id" element={<AdminCategoryView />} />
            <Route path="categories/:id/edit" element={<AdminCategoryForm />} />
            <Route path="categories/:id/properties" element={<AdminCategoryProperties />} />
            <Route path="categories/:id/subcategories" element={<AdminCategorySubCategories />} />
            <Route path="topics" element={<AdminTopics />} />
            <Route path="topics/add" element={<AdminTopicForm />} />
            <Route path="topics/:id" element={<AdminTopicView />} />
            <Route path="topics/:id/edit" element={<AdminTopicForm />} />
            <Route path="countries" element={<AdminCountries />} />
            <Route path="countries/add" element={<AdminCountryForm />} />
            <Route path="countries/:id/edit" element={<AdminCountryForm />} />
            <Route path="cities" element={<AdminCities />} />
            <Route path="cities/add" element={<AdminCityForm />} />
            <Route path="cities/:id/edit" element={<AdminCityForm />} />
            <Route path="banners" element={<AdminBanners />} />
            <Route path="banners/add" element={<AdminBannerForm />} />
            <Route path="banners/:id/edit" element={<AdminBannerForm />} />
            <Route path="ad-categories" element={<AdminAdCategories />} />
            <Route path="ad-categories/add" element={<AdminAdCategoryForm />} />
            <Route path="ad-categories/:id/edit" element={<AdminAdCategoryForm />} />
            <Route path="ad-categories/:id/custom-fields" element={<AdminAdCategoryCustomFields />} />
            <Route path="ad-categories/:id/custom-filters" element={<AdminAdCategoryCustomFilters />} />
            <Route path="ad-types" element={<AdminAdTypes />} />
            <Route path="ad-types/add" element={<AdminAdTypeForm />} />
            <Route path="ad-types/:id/edit" element={<AdminAdTypeForm />} />

            <Route path="interests" element={<AdminInterests />} />
            <Route path="interests/add" element={<AdminInterestForm />} />
            <Route path="interests/:id/edit" element={<AdminInterestForm />} />

            <Route path="strategic-ad-types" element={<AdminStrategicAdTypes />} />
            <Route path="strategic-ad-types/add" element={<AdminStrategicAdTypeForm />} />
            <Route path="strategic-ad-types/:id/edit" element={<AdminStrategicAdTypeForm />} />
            
            <Route path="ad-packages" element={<AdminAdPackages />} />
            <Route path="ad-packages/add" element={<AdminAdPackageForm />} />
            <Route path="ad-packages/:id/edit" element={<AdminAdPackageForm />} />
            <Route path="ads" element={<AdminAds />} />
            <Route path="ads/:id" element={<AdminAdDetails />} />
            <Route path="system-users" element={<AdminSystemUsers />} />
            <Route path="system-users/add" element={<AdminSystemUserForm />} />
            <Route path="system-users/:id/edit" element={<AdminSystemUserForm />} />
            <Route path="roles" element={<AdminRoles />} />
            <Route path="roles/add" element={<AdminRoleForm />} />
            <Route path="roles/:id/edit" element={<AdminRoleForm />} />
            <Route path="roles/:id/permissions" element={<AdminRolePermissions />} />
            <Route path="permissions" element={<AdminPermissions />} />
            <Route path="account" element={<AdminAccount />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="contacts" element={<AdminContacts />} />

            {/* Fesilah Cards */}
            <Route path="fesilah-cards" element={<AdminVoucherBatches type="card" />} />
            <Route path="fesilah-cards/add" element={<AdminVoucherBatchForm type="card" />} />
            <Route path="fesilah-cards/:id/edit" element={<AdminVoucherBatchForm type="card" />} />
            <Route path="fesilah-cards/:id/vouchers" element={<AdminBatchVouchers type="card" />} />

            {/* Fesilah Vouchers */}
            <Route path="fesilah-vouchers" element={<AdminVoucherBatches type="voucher" />} />
            <Route path="fesilah-vouchers/add" element={<AdminVoucherBatchForm type="voucher" />} />
            <Route path="fesilah-vouchers/:id/edit" element={<AdminVoucherBatchForm type="voucher" />} />
            <Route path="fesilah-vouchers/:id/vouchers" element={<AdminBatchVouchers type="voucher" />} />

            {/* Agricultural Influencers */}
            <Route path="agricultural-influencers" element={<AdminAgriculturalInfluencers />} />
            <Route path="agricultural-influencers/add" element={<AdminAgriculturalInfluencerForm />} />
            <Route path="agricultural-influencers/:id/edit" element={<AdminAgriculturalInfluencerForm />} />

            {/* Reviews */}
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="reviews/:id" element={<AdminReviewDetails />} />

            {/* Notifications */}
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="notifications/send" element={<AdminNotificationForm />} />

            {/* Promo Codes */}
            <Route path="promo-codes" element={<AdminPromoCodes />} />
            <Route path="promo-codes/add" element={<AdminPromoCodeForm />} />
            <Route path="promo-codes/:id" element={<AdminPromoCodeDetails />} />
            <Route path="promo-codes/:id/edit" element={<AdminPromoCodeForm />} />

            {/* Finance & Payments */}
            <Route path="finance" element={<AdminFinance />} />

            {/* Activity Logs */}
            <Route path="activity-logs" element={<AdminActivityLogs />} />
          </Route>

          {/* Doctor Routes */}
          <Route path="/doctor/login" element={<DoctorLogin />} />
          <Route
            path="/doctor"
            element={
              <ProtectedRoute allowedUserTypes={['doctor']}>
                <DoctorLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<DoctorDashboard />} />
            <Route path="tickets" element={<DoctorTickets />} />
            <Route path="new-tickets" element={<DoctorNewTickets />} />
            <Route path="ticket/:id" element={<DoctorTicketChat />} />
            <Route path="account" element={<DoctorAccount />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </Provider>
  );
}

export default App;
